import React, { useState } from "react";
import { walkaroundTemplates, WalkaroundItem, WalkaroundTemplate } from "../data/walkaroundAudits";
import { auditApi } from "../services/api";

interface ItemRecord {
  status: string;
  value: string;
  note: string;
  evidence: string[];
}

export interface WalkaroundRecord {
  id: string;
  templateId: string;
  templateName: string;
  startedAt: string;
  completedAt: string;
  completedBy: string;
  items: Record<string, ItemRecord>;
  score: number;
}

const STORE_KEY = "walkaround_records";

function loadHistory(): WalkaroundRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function emptyRecord(template: WalkaroundTemplate): Record<string, ItemRecord> {
  const items: Record<string, ItemRecord> = {};

  template.sections.forEach(section =>
    section.items.forEach(item => {
      items[item.id] = { status: "", value: "", note: "", evidence: [] };
    })
  );

  return items;
}

export default function Walkarounds({ user }: { user?: any }) {
  const [templateId, setTemplateId] = useState("wr-hourly");
  const [records, setRecords] = useState<Record<string, ItemRecord>>(() => emptyRecord(walkaroundTemplates[0]));
  const [history, setHistory] = useState<WalkaroundRecord[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [message, setMessage] = useState("");

  const template = walkaroundTemplates.find(item => item.id === templateId) || walkaroundTemplates[0];

  const switchTemplate = (id: string) => {
    const next = walkaroundTemplates.find(item => item.id === id);

    if (!next) return;

    setTemplateId(id);
    setRecords(emptyRecord(next));
    setMessage("");
  };

  const updateItem = (itemId: string, patch: Partial<ItemRecord>) => {
    setRecords(current => ({ ...current, [itemId]: { ...current[itemId], ...patch } }));
  };

  const attachEvidence = async (itemId: string, file: File) => {
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", file.type.startsWith("video") ? "video" : "photo");
      form.append("context", `walkaround:${templateId}:${itemId}`);
      await auditApi.uploadEvidence(form);
      updateItem(itemId, { evidence: [...(records[itemId]?.evidence || []), `${file.name} (uploaded)`] });
      setMessage("Evidence uploaded to server.");
    } catch (error) {
      updateItem(itemId, { evidence: [...(records[itemId]?.evidence || []), `${file.name} (saved locally)`] });
      setMessage("Upload not available, evidence saved with the record.");
    }
  };

  const completeWalkaround = () => {
    let pass = 0;
    let fail = 0;
    let total = 0;

    template.sections.forEach(section =>
      section.items.forEach(item => {
        const rec = records[item.id];

        if (!rec) return;

        total += 1;
        if (rec.status === "pass") pass += 1;
        if (rec.status === "fail") fail += 1;
      })
    );

    const score = total === 0 ? 0 : Math.round((pass / total) * 100);

    const record: WalkaroundRecord = {
      id: `${template.id}-${Date.now()}`,
      templateId: template.id,
      templateName: template.name,
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      completedBy: user && user.name ? user.name : "Unknown",
      items: records,
      score,
    };

    const nextHistory = [record, ...history];

    setHistory(nextHistory);
    localStorage.setItem(STORE_KEY, JSON.stringify(nextHistory));
    setMessage(`Walkaround saved. Score ${score}% with ${fail} failed items.`);
    setRecords(emptyRecord(template));
  };
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Walkarounds</h2>
          <p className="text-sm text-slate-600">{template.description}</p>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="rounded-lg border border-blue-900 px-4 py-2 text-sm font-medium text-blue-900"
        >
          {showHistory ? "Back to checklist" : `History (${history.length})`}
        </button>
      </div>

      {showHistory ? (
        <div className="space-y-3">
          {history.map(record => (
            <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{record.templateName}</div>
                <div className="text-sm text-slate-600">Score {record.score}%</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {new Date(record.completedAt).toLocaleString()} by {record.completedBy}
              </div>
            </div>
          ))}
          {history.length === 0 ? <div className="text-sm text-slate-500">No walkarounds recorded yet.</div> : null}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {walkaroundTemplates.map(item => (
              <button
                key={item.id}
                onClick={() => switchTemplate(item.id)}
                className={
                  item.id === templateId
                    ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white"
                    : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
                }
              >
                {item.name}
              </button>
            ))}
          </div>

          {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

          {template.sections.map(section => (
            <div key={section.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-semibold text-slate-900">{section.title}</h3>
              <div className="space-y-4">
                {section.items.map(item => {
                  const rec = records[item.id] || { status: "", value: "", note: "", evidence: [] };

                  return (
                    <div key={item.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="text-sm font-medium text-slate-800">{item.label}</div>
                      {item.hint ? <div className="text-xs text-slate-500">{item.hint}</div> : null}

                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button onClick={() => updateItem(item.id, { status: "pass" })} className={rec.status === "pass" ? "rounded-lg bg-green-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Pass</button>
                        <button onClick={() => updateItem(item.id, { status: "fail" })} className={rec.status === "fail" ? "rounded-lg bg-red-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Fail</button>
                        <button onClick={() => updateItem(item.id, { status: "na" })} className={rec.status === "na" ? "rounded-lg bg-slate-600 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>N/A</button>

                        {item.type === "reading" ? (
                          <input value={rec.value} onChange={event => updateItem(item.id, { value: event.target.value })} placeholder="Value" className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                        ) : null}

                        <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700">
                          Add photo
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attachEvidence(item.id, file); }} />
                        </label>
                        <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700">
                          Add video
                          <input type="file" accept="video/*" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attachEvidence(item.id, file); }} />
                        </label>
                      </div>

                      <input value={rec.note} onChange={event => updateItem(item.id, { note: event.target.value })} placeholder="Note" className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                      {rec.evidence.length > 0 ? <div className="mt-1 text-xs text-slate-500">{rec.evidence.join(", ")}</div> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button onClick={completeWalkaround} className="rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">
            Complete and save walkaround
          </button>
        </>
      )}
    </div>
  );
}